import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { submitFeedback } from '../services/adminService';

// The 5 questions in the order they appear on the form.
const QUESTIONS = [
  {
    key: 'q1',
    title: 'How was your user experience on our web app?',
    hint: 'Easy to navigate? Smooth on mobile? Anything confusing?',
  },
  {
    key: 'q2',
    title: 'Do you need any other services along with Recruiter Profile Certification?',
    hint: 'Rate how interested you are in additional services. Use the text box to tell us which services would help you most.',
  },
  {
    key: 'q3',
    title: 'How was your delivery experience?',
    hint: 'On time, well-formatted, easy to apply to LinkedIn?',
  },
  {
    key: 'q4',
    title: 'How well did our process explain what was happening at each step?',
    hint: 'Order confirmation, intake form, expert reviews, delivery.',
  },
  {
    key: 'q5',
    title: 'How likely are you to recommend Successlane to a friend or colleague?',
    hint: '1 = not at all, 5 = very likely.',
  },
];

const Star = ({ filled, hover, onClick, onMouseEnter, onMouseLeave }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    aria-label={`${filled ? 'Filled' : 'Empty'} star`}
    style={{
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      fontSize: '28px',
      lineHeight: 1,
      color: filled || hover ? '#f59e0b' : '#cbd5e1',
      transition: 'color 0.12s',
    }}
  >
    ★
  </button>
);

const FeedbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Pre-fill order id if provided via ?order=KA000042 (from delivery email link)
  const initialOrder = searchParams.get('order') || '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState(initialOrder);
  const [ratings, setRatings] = useState({});
  const [hovers, setHovers] = useState({});
  const [comments, setComments] = useState({});
  const [extra, setExtra] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const setRating = (q, n) => setRatings(prev => ({ ...prev, [q]: n }));
  const setComment = (q, v) => setComments(prev => ({ ...prev, [q]: v }));
  const setHover = (q, n) => setHovers(prev => ({ ...prev, [q]: n }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const anyRating = QUESTIONS.some(q => ratings[q.key]);
    if (!anyRating) {
      setError('Please rate at least one question before sharing.');
      return;
    }
    const trimmedEmail = email.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

    // Build payload; omit empty/null fields entirely so pydantic never sees
    // a string that fails EmailStr / other validators.
    const payload = {};
    if (name.trim())      payload.user_name     = name.trim();
    if (isValidEmail)     payload.user_email    = trimmedEmail;
    if (orderId.trim())   payload.order_id      = orderId.trim();
    if (extra.trim())     payload.extra_comment = extra.trim();

    QUESTIONS.forEach(q => {
      const r = Number(ratings[q.key]);
      if (r >= 1 && r <= 5) payload[`${q.key}_rating`] = r;
      const c = (comments[q.key] || '').trim();
      if (c)                payload[`${q.key}_comment`] = c;
    });

    setSubmitting(true);
    try {
      await submitFeedback(payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ maxWidth: 460, background: '#fff', borderRadius: 16, padding: '40px 32px', boxShadow: '0 8px 32px rgba(0,0,0,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🙏</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Thanks for sharing!</h1>
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
            Your feedback helps us get better. We read every response — if there's a follow-up needed, we'll reach out on the email you provided.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{ padding: '10px 22px', background: '#1a6fd6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '11px 13px',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 14,
    color: '#0f172a',
    background: '#fff',
    outline: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FFFBEB', padding: '40px 16px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ width: 10, height: 10, background: '#1a6fd6', borderRadius: '50%' }}></span>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>Successlane</span>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', border: '1px solid #e2e8f0', marginBottom: 16 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Share your feedback</h1>
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 0 }}>
            We read every response. Rate the parts that apply and add a comment if you'd like to expand. Only one rating is required.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Identity */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 22, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>About you (optional)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
              <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
              <input type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
              <input type="text" placeholder="Order ID (if applicable)" value={orderId} onChange={e => setOrderId(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Questions */}
          {QUESTIONS.map((q, idx) => (
            <div key={q.key} style={{ background: '#fff', borderRadius: 12, padding: 22, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>{`QUESTION ${idx + 1} OF 5`}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 4, lineHeight: 1.4 }}>{q.title}</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 14, lineHeight: 1.5 }}>{q.hint}</div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 12 }}>
                {[1, 2, 3, 4, 5].map(n => {
                  const filled = (ratings[q.key] || 0) >= n;
                  const hover = (hovers[q.key] || 0) >= n;
                  return (
                    <Star
                      key={n}
                      filled={filled}
                      hover={hover}
                      onClick={() => setRating(q.key, n)}
                      onMouseEnter={() => setHover(q.key, n)}
                      onMouseLeave={() => setHover(q.key, 0)}
                    />
                  );
                })}
                {ratings[q.key] && (
                  <span style={{ fontSize: 13, color: '#475569', marginLeft: 8 }}>{ratings[q.key]} / 5</span>
                )}
              </div>

              <textarea
                placeholder="Tell us more (optional)"
                value={comments[q.key] || ''}
                onChange={e => setComment(q.key, e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              />
            </div>
          ))}

          {/* Extra comment */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 22, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Anything else you'd like to share?</div>
            <textarea
              placeholder="Optional"
              value={extra}
              onChange={e => setExtra(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
            />
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '13px 16px',
              background: submitting ? '#94a3b8' : '#1a6fd6',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer',
            }}
            data-testid="feedback-share-btn"
          >
            {submitting ? 'Sharing…' : 'Share my feedback'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 16 }}>
            Successlane · <a href="mailto:contact@successlane.io" style={{ color: '#1a6fd6', textDecoration: 'none' }}>contact@successlane.io</a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default FeedbackPage;
